import React, { useState } from 'react';
import Link from 'next/link';
import { Row, Col, Typography } from 'antd';
import Head from 'next/head';
import { motion } from 'framer-motion';
import DMARCDescription from '@/components/DMARCDescription';
import DMARCTable from '@/components/DMARCTable';
import DMARCPricing from '@/components/DMARCPricing';
import DMARCFAQ from '@/components/DMARCFAQ';
import { dmarcProducts } from '@/data/dmarc-products';

const { Title, Paragraph, Text } = Typography;

const DMARCEnforcement = () => {
  const [selectedOption, setSelectedOption] = useState({
    'dmarc-plus': '100,000',
    'dmarc-premium': '100,000',
  });

  const handleOptionChange = (newOptions) => {
    setSelectedOption(newOptions);
  };

  const getSelectedProductOption = (productId) => {
    const product = dmarcProducts.find((p) => p.id === productId);
    if (!product) return null;

    const option = product.options.find((option) => option.emails === selectedOption[productId]);
    // Return first option as default if no match found
    return option || product.options[0];
  };

  // Get starting price from the Plus plan's lowest option
  const startingPrice = dmarcProducts.find((p) => p.id === 'dmarc-plus').options[0].price;

  return (
    <div>
      <Head>
        <title>EasyDMARC Enforcement for Email Security | Brandymail</title>
        <meta
          name="description"
          content="Protect your domain from email abuse with EasyDMARC Enforcement. Improve email deliverability, prevent spoofing, and meet Gmail and Yahoo! compliance requirements."
        />
        <meta property="og:title" content="EasyDMARC Enforcement for Email Security | Brandymail" />
        <meta
          property="og:description"
          content="Protect your domain from email abuse with EasyDMARC Enforcement. Improve email deliverability, prevent spoofing, and meet Gmail and Yahoo! compliance requirements."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://brandymail.com/products/easydmarc-enforcement" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="EasyDMARC Enforcement for Email Security | Brandymail"
        />
        <meta
          name="twitter:description"
          content="Protect your domain from email abuse with EasyDMARC Enforcement. Improve email deliverability, prevent spoofing, and meet Gmail and Yahoo! compliance requirements."
        />
        <meta name="twitter:image" content="https://brandymail.com/images/brandymail-dmarc.svg" />
      </Head>
      <section className="products section hero v9">
        <div className="container-default w-container">
          <div className="position-relative z-index-1">
            <div className="mg-bottom-78px">
              <motion.div
                className="inner-container _861px center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
              >
                <div className="inner-container _600px---tablet center">
                  <motion.div
                    className="text-center"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2, ease: 'easeOut' }}
                  >
                    <div className="mg-bottom-24px">
                      <div className="text-100 bold color-accent-1 text-uppercase">
                        STARTING AT ${startingPrice}/MONTH
                      </div>
                    </div>
                    <h1 className="display-1 color-neutral-100 mg-bottom-24px">
                      DMARC Enforcement
                    </h1>
                    <p className="color-neutral-300 mg-bottom-0 paragraph-large">
                      Prevent bad actors from sending unauthorized emails from your domain. With our
                      DMARC Enforcement solutions from EasyDMARC, you'll be able to mitigate risk of
                      email abuse, improve email deliverability so your message lands in the inbox,
                      and reach compliance with new Gmail and Yahoo! email authentication
                      requirements.
                    </p>
                  </motion.div>
                </div>
              </motion.div>
            </div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <DMARCPricing
                products={dmarcProducts}
                selectedOption={selectedOption}
                onOptionChange={handleOptionChange}
                getSelectedProductOption={getSelectedProductOption}
              />
            </motion.div>
          </div>
        </div>

        <div className="position-absolute dark-fade---button pricing-fade"></div>
        <div className="position-absolute card-top-bg---bottom pricing-bg"></div>
      </section>

      <div className="overflow-hidden border-radius-40px---bottom">
        <section className="section pd-top-0px bg-neutral-100 pd-0px">
          <div className="container-default w-container inner-container center">
            <DMARCTable
              products={dmarcProducts}
              selectedOption={selectedOption}
              getSelectedProductOption={getSelectedProductOption}
            />
          </div>
        </section>
      </div>

      <DMARCFAQ />
    </div>
  );
};

export default DMARCEnforcement;
